import React, { useState } from 'react';
import { Calendar, BookOpen, Package, Filter } from 'lucide-react';
import { Button } from '../ui/button';

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
  'July', 'August', 'September', 'October', 'November', 'December'];

export const AdminRevenue = ({ bookings, orders }) => {
  const [revenueSubTab, setRevenueSubTab] = useState('all');
  const [revenueFilterType, setRevenueFilterType] = useState('month');
  const [revenueMonth, setRevenueMonth] = useState('');
  const [revenueYear, setRevenueYear] = useState(new Date().getFullYear());
  const [revenueDateFrom, setRevenueDateFrom] = useState('');
  const [revenueDateTo, setRevenueDateTo] = useState('');

  return (
    <div className="space-y-6">
      <div className="flex gap-2 mb-4 flex-wrap">
        {['all', 'bookings', 'ebooks', 'souvenirs'].map(tab => (
          <button key={tab} onClick={() => setRevenueSubTab(tab)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              revenueSubTab === tab ? 'bg-[#8B1818] text-white' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}>
            {tab === 'bookings' && <Calendar className="h-4 w-4" />}
            {tab === 'ebooks' && <BookOpen className="h-4 w-4" />}
            {tab === 'souvenirs' && <Package className="h-4 w-4" />}
            {tab === 'all' ? 'All Revenue' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700">Filter Type:</label>
            <select value={revenueFilterType} onChange={(e) => setRevenueFilterType(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              <option value="month">Month/Year</option>
              <option value="range">Date Range</option>
            </select>
          </div>
          {revenueFilterType === 'month' ? (
            <>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700">Month:</label>
                <select value={revenueMonth} onChange={(e) => setRevenueMonth(e.target.value)}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">All Months</option>
                  {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700">Year:</label>
                <select value={revenueYear} onChange={(e) => setRevenueYear(e.target.value)}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700">From:</label>
                <input type="date" value={revenueDateFrom} onChange={(e) => setRevenueDateFrom(e.target.value)}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700">To:</label>
                <input type="date" value={revenueDateTo} onChange={(e) => setRevenueDateTo(e.target.value)}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </>
          )}
          <Button className="bg-[#8B1818] text-white"><Filter className="h-4 w-4 mr-2" /> Apply Filter</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-[#8B1818] to-[#701313] rounded-xl p-6 text-white">
          <h3 className="text-lg font-semibold opacity-90">Total Revenue</h3>
          <p className="text-3xl font-bold mt-2">
            &#8369;{(
              bookings.filter(b => ['approved', 'completed'].includes(b.status)).reduce((sum, b) => sum + (b.total_amount || 0), 0) +
              orders.filter(o => ['approved', 'shipped', 'completed'].includes(o.status)).reduce((sum, o) => sum + (o.total_amount || 0), 0)
            ).toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-sm text-slate-600">Bookings Revenue</h3>
          <p className="text-2xl font-bold text-slate-900">&#8369;{bookings.filter(b => ['approved', 'completed'].includes(b.status)).reduce((sum, b) => sum + (b.total_amount || 0), 0).toLocaleString()}</p>
          <p className="text-xs text-slate-500">{bookings.filter(b => ['approved', 'completed'].includes(b.status)).length} transactions</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-sm text-slate-600">eBooks Revenue</h3>
          <p className="text-2xl font-bold text-slate-900">&#8369;{orders.filter(o => ['approved', 'shipped', 'completed'].includes(o.status))
            .reduce((sum, o) => sum + (o.items?.filter(i => i.category === 'ebook').reduce((s, i) => s + (i.price * i.quantity), 0) || 0), 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-sm text-slate-600">Souvenirs Revenue</h3>
          <p className="text-2xl font-bold text-slate-900">&#8369;{orders.filter(o => ['approved', 'shipped', 'completed'].includes(o.status))
            .reduce((sum, o) => sum + (o.items?.filter(i => i.category === 'souvenir').reduce((s, i) => s + (i.price * i.quantity), 0) || 0), 0).toLocaleString()}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-xl font-bold text-slate-900">
            {revenueSubTab === 'all' ? 'All' : revenueSubTab === 'bookings' ? 'Booking' : revenueSubTab === 'ebooks' ? 'eBook' : 'Souvenir'} Revenue Details
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-4 font-semibold text-slate-900">ID</th>
                <th className="text-left p-4 font-semibold text-slate-900">Type</th>
                <th className="text-left p-4 font-semibold text-slate-900">Customer</th>
                <th className="text-left p-4 font-semibold text-slate-900">Date</th>
                <th className="text-left p-4 font-semibold text-slate-900">Status</th>
                <th className="text-right p-4 font-semibold text-slate-900">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(revenueSubTab === 'all' || revenueSubTab === 'bookings') && bookings
                .filter(b => ['approved', 'completed'].includes(b.status))
                .map((booking) => (
                  <tr key={`booking-${booking.id}`} className="border-t border-slate-100">
                    <td className="p-4 text-sm font-mono">{booking.id?.slice(0, 8).toUpperCase()}</td>
                    <td className="p-4"><span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Booking</span></td>
                    <td className="p-4"><p className="font-medium">{booking.user_name}</p><p className="text-sm text-slate-500">{booking.user_email}</p></td>
                    <td className="p-4 text-sm">{booking.date}</td>
                    <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs ${booking.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{booking.status}</span></td>
                    <td className="p-4 text-right font-semibold text-[#8B1818]">&#8369;{booking.total_amount?.toLocaleString()}</td>
                  </tr>
                ))}
              {(revenueSubTab === 'all' || revenueSubTab === 'ebooks' || revenueSubTab === 'souvenirs') && orders
                .filter(o => ['approved', 'shipped', 'completed'].includes(o.status))
                .filter(o => {
                  if (revenueSubTab === 'ebooks') return o.items?.some(i => i.category === 'ebook');
                  if (revenueSubTab === 'souvenirs') return o.items?.some(i => i.category === 'souvenir');
                  return true;
                })
                .map((order) => (
                  <tr key={`order-${order.id}`} className="border-t border-slate-100">
                    <td className="p-4 text-sm font-mono">{order.id?.slice(0, 8).toUpperCase()}</td>
                    <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${order.items?.some(i => i.category === 'ebook') ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'}`}>{order.items?.some(i => i.category === 'ebook') ? 'eBook' : 'Souvenir'}</span></td>
                    <td className="p-4"><p className="font-medium">{order.user_name}</p><p className="text-sm text-slate-500">{order.user_email}</p></td>
                    <td className="p-4 text-sm">{new Date(order.created_at).toLocaleDateString()}</td>
                    <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs ${order.status === 'completed' ? 'bg-green-100 text-green-800' : order.status === 'shipped' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>{order.status}</span></td>
                    <td className="p-4 text-right font-semibold text-[#8B1818]">&#8369;{order.total_amount?.toLocaleString()}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
